import React, { useState, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, Outlet, useLocation, useMatch } from "react-router-dom";
import classnames from "classnames";

import Config from "@skin_config";
import Loader from "@common/components/loader";
import IdleService from "@common/services/idle";
import Helpers from "@common/helpers/helperFunctions";

import PullToReload from "@common/components/reload";
import { checkTerminalMode } from "@common/helpers";
import { changeSecondScreen } from "@common/helpers/secondScreen";
import { POPUPS } from "@common/constants/popups";
import Header from "../header";
import { Popup } from "../popup";
import BetslipWrapper from "../betslipWrapper";
import SecondScreen from "../../components/secondScreen";
import InitMixin from "../../../mixins/initMixin";
import ProfileHandler from "../../components/userProfileSubscriber";
import KeyboardForCasino from "../../components/keyboardForCasino/index";
import { PopupContext } from "@app/dev/contexts/popup";
import { UIContext } from "@app/dev/contexts/ui";
import IdleBanner from '@app/dev/images/circus-idle-banner.jpg'
import { handleIdle } from "@app/dev/helpers/idle";
import { Footer } from "../../components/footer";
import { SetTerminalUsageConfirmed } from "@app/dev/actions/terminal";
import './style.scss';

let isFirstTimeInHome = true;

const Layout = ({ appReady, ...props }) => {
  const dispatch = useDispatch();
  const showKeyboard = useSelector((state) => state.terminal.showKeyboard);
  const verification = useSelector((state) => state.terminal.verification);
  const { verification: userVerification } = useSelector((state) => state.user);
  const [showIdleImage, setShowIdleImage] = useState(Config.main.homeIdleTimeInMinutes && isFirstTimeInHome);
  const terminalUsageConfirmed = useSelector(state => state.terminal.terminalUsageConfirmed);
  const isLocked = useSelector((state) => state.terminal.popupLauncher?.lockTerminal);

  const [{ closePopup, openPopup }] = useContext(PopupContext);
  const [{ theme }] = useContext(UIContext);

  const isCasinoPage = useMatch('/casino/*');

  useEffect(() => {
    if (Config.main.homeIdleTimeInMinutes) {
      setShowIdleImage(!isLocked);
      if (!isLocked) dispatch(SetTerminalUsageConfirmed(false));
    }
  }, [dispatch, isLocked]);

  useEffect(() => {
    changeSecondScreen(Config, {
      command: "Default",
      id: 0,
      url: "",
      providerId: 0,
    });

    const clearTimer = IdleService.ifInactive(30, handleIdle, false);

    return () => clearTimer();
  }, []);

  useEffect(() => {
    if (userVerification?.success) IdleService.resetTimer();
  }, [userVerification]);

  useEffect(() => {
    let clearTimer;

    if (verification.isVerified && !isLocked) {
      IdleService.resetTimer();
      clearTimer = IdleService.ifInactive(
        Config.main.verificationInactiveTimeInSeconds + 1,
        () => openPopup(POPUPS.PASSPORT_VERIFICATION_INACTIVE),
        false
      );
    } else clearTimer?.();

    return () => {
      clearTimer?.();
    };
  }, [verification.isVerified, isLocked]);

  useEffect(() => {
    isFirstTimeInHome = false;

    if (Config.main.homeIdleTimeInMinutes && !showIdleImage) {
      const clearTimer = IdleService.ifInactive(Config.main.homeIdleTimeInMinutes * 60, () => {
        if (!isLocked) {
          dispatch(SetTerminalUsageConfirmed(false));
          setShowIdleImage(true);
          closePopup();
        }
      });

      return () => clearTimer();
    }

    return () => { };
  }, [dispatchEvent, showIdleImage, isLocked]);

  const handleClick = () => {
    Helpers.hasClassName(".main-wrapper", "betslip") && Helpers.removeClassName(".main-wrapper", "betslip");
  };

  const location = useLocation();

  const disabledHeader = location.pathname.includes("/admin") || checkTerminalMode(Config, "clientTransaction");

  const disabledBetslip =
    Config.betslipDisabledUrls.some((path) => matchPath(path, location.pathname)) ||
    checkTerminalMode(Config, "clientTransaction");

  const onIdleImageClick = () => {
    setShowIdleImage(false);
    if (Config.main.enableTerminalUsageConfirmation && !terminalUsageConfirmed) {
      openPopup(POPUPS.TERMINAL_USAGE_CONFIRMATION_POPUP);
    }
  };

  if (showIdleImage) {
    return (
      <div className="home-idle-image" onClick={onIdleImageClick}>
        <img src={IdleBanner} />
      </div>
    );
  }

  return appReady ? ( // start rendering only when application is ready (translations/config/etc are loaded)
    <>
      {Config.main.enableReloadBySwipe && <PullToReload />}
      <SecondScreen />
      <div
        className={classnames("terminal-wrapper", `terminal-mode-${Config.main.terminalMode}`, `app-theme-${theme}`, {
          "betslip-active": !disabledBetslip,
          "split-screen": Helpers.isSplitScreenAvailable(Config),
          "winter-mode": Config.main.enableWinterMode,
        })}
        onClick={handleClick}
      >
        {/* <img src={SantaPic} className="santa-gif" /> */}
        {!disabledHeader ? <Header {...props} /> : null}
        <div className="content-wrapper-t">
          <div
            className={`scroll-container-t ${isCasinoPage && "casino"} ${location.pathname.includes("thirdPartyGame") && "lottery"
              } ${location.pathname.includes("/prematch/all/multiples") && "multiples-style"} ${Config.terminal.scrollButtons && "scroll-b-view"
              } ${location.pathname === "/" && "home-pages"} ${Config.footer.footerEnabled && "with-footer"}`}
          >
            <Outlet />
            {props.user && !props.user.loginInProgress && props.user.loggedIn ? <ProfileHandler /> : null}
            {!disabledBetslip && <BetslipWrapper />}
          </div>
          {Config.footer.footerEnabled && <Footer />}
        </div>
        <Popup />
        {showKeyboard && <KeyboardForCasino />}
      </div>
    </>
  ) : (
    <Loader />
  );
};

export default InitMixin({ Component: Layout });
